import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { formatNotification } from '../utils/notifications/notificationTypes';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved === null ? true : saved === 'true';
  });

  // Maximum number of notifications to show at once
  const MAX_NOTIFICATIONS = 5;

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;

    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, [soundEnabled]);

  // Show a notification
  const showNotification = useCallback((notificationData) => {
    const formattedNotification = formatNotification(notificationData);

    setNotifications((prev) => {
      // Check if notification with same ID already exists
      if (prev.some((n) => n.id === formattedNotification.id)) {
        return prev;
      }

      // Add new notification at the beginning
      const updated = [formattedNotification, ...prev];

      // Keep only MAX_NOTIFICATIONS
      return updated.slice(0, MAX_NOTIFICATIONS);
    });

    // Play sound if enabled
    if (formattedNotification.sound) {
      playNotificationSound();
    }

    return formattedNotification.id;
  }, [playNotificationSound]);

  // Dismiss a notification
  const dismissNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  // Dismiss all notifications
  const dismissAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  // Toggle sound
  const toggleSound = useCallback((enabled) => {
    setSoundEnabled(enabled);
    localStorage.setItem('notificationSoundEnabled', enabled.toString());
  }, []);

  // Get unread count
  const getUnreadCount = useCallback(() => {
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  // Auto-cleanup old notifications (older than 1 hour)
  useEffect(() => {
    const cleanup = setInterval(() => {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      
      setNotifications((prev) =>
        prev.filter((n) => new Date(n.timestamp) > oneHourAgo)
      );
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(cleanup);
  }, []);

  const value = {
    notifications,
    showNotification,
    dismissNotification,
    dismissAll,
    markAsRead,
    soundEnabled,
    toggleSound,
    getUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
